import { makeAutoObservable } from 'mobx';

import { GridStore } from 'controls/grid/gridStore';
import { RemoteDataProvider } from 'controls/grid/remoteDataProvider';
import { GridConfiguration, GridSelectionType } from 'controls/grid/gridConfiguration';
import { MobxManager } from 'framework/mobx-integration';
import { GridColumnConfig } from 'controls/grid/gridColumnConfig';
import { getDataRegistry, GetDataRegistryArgs, getDataRegistryFilters } from 'areas/management/data-registry/api';
import { DataRegistryEntry } from 'areas/management/data-registry/dataRegistryEntry';
import { i18n } from './localization';
import { UserSettingsStateProvider } from 'controls/grid/stateProviders';
import { DraggablePlugin } from 'controls/grid/plugins/draggablePlugin';
import { EntityType } from 'controls/designer/features/ceeview-entity-drop/types';
import { ApplicationState } from 'framework/applicationState';

type DataRegistryStoreProps = {
	accountId?: string;
	defaultSelectedMetricIds?: string[];
	onAdd?: (ids: string[]) => void;
	onClose?: () => void;
	limit?: number;
	singleMetricSelection?: boolean;
	includeSubaccounts?: boolean;
};

enum DataRegistryLayout {
	Asset = 'asset',
	Service = 'service',
	Monitor = 'monitor',
}

type DataRegistryInitDefaultArgs = {
	onConfigCreated?: (config: GridConfiguration<DataRegistryEntry>) => void;
	userSettingsPrefix?: string;
	draggable?: boolean;
};

export class DataRegistryStore {
	props: DataRegistryStoreProps;
	gridStore: GridStore<DataRegistryEntry>;

	showOnlySelected: boolean = false;

	mobx = new MobxManager();

	selectedLayout: DataRegistryLayout = DataRegistryLayout.Asset;

	constructor(props?: DataRegistryStoreProps) {
		this.props = props ?? {};

		makeAutoObservable(this);
	}

	get layouts() {
		return [
			{ label: i18n('Asset'), value: DataRegistryLayout.Asset },
			{ label: i18n('Monitor'), value: DataRegistryLayout.Monitor },
			{ label: i18n('Service'), value: DataRegistryLayout.Service },
		];
	}

	getDefaultDataSource(args?: GetDataRegistryArgs) {
		return getDataRegistry(
			args ?? {
				accountId: this.props.accountId,
				includeSubaccounts: this.props.includeSubaccounts ?? ApplicationState.includeSubaccounts,
				selection: this.showOnlySelected ? this.gridStore.selection.payload : null,
			},
		);
	}

	getDefaultFilters() {
		return getDataRegistryFilters();
	}

	initDefault(args?: DataRegistryInitDefaultArgs) {
		this.mobx.reaction(
			() => [this.selectedLayout],
			() => {
				const selectionState = this.gridStore?.selection.getState() ?? {
					ids: this.props.defaultSelectedMetricIds,
				};

				const gridConfigActual: GridConfiguration<DataRegistryEntry> = {
					columns: this.getColumns(),
					dataProvider: new RemoteDataProvider({
						dataSource: () => this.getDefaultDataSource(),
					}),
					filtersConfiguration: this.getDefaultFilters(),
					defaults: {
						selectionState: selectionState,
						showInlineFilters: true,
					},
					plugins: args?.draggable ? [this.getDraggablePlugin()] : [],
					selection: this.props.singleMetricSelection ? GridSelectionType.One : GridSelectionType.Many,
				};

				if (args?.userSettingsPrefix) {
					gridConfigActual.stateProviders = [
						new UserSettingsStateProvider({
							category: args.userSettingsPrefix + '-data-registry-' + this.selectedLayout,
						}),
					];
				}

				args?.onConfigCreated?.(gridConfigActual);

				this.gridStore = new GridStore<DataRegistryEntry>(gridConfigActual);
			},
			{
				fireImmediately: true,
			},
		);
	}

	get limit() {
		return this.props.limit ?? 100;
	}

	getDraggablePlugin() {
		return new DraggablePlugin<DataRegistryEntry>({
			entityType: EntityType.Metric,
		});
	}

	getColumns() {
		const columns: GridColumnConfig<DataRegistryEntry>[] = [
			{
				field: 'agentName',
				width: 250,
				renderer: (r) => r.agentName,
			},
			{
				field: 'metricId',
				width: 250,
				renderer: (r) => r.metricId,
			},
			{
				field: 'metricType',
				width: 250,
				renderer: (r) => r.metricType,
			},
			{
				field: 'registryIdentifier',
				width: 250,
				renderer: (r) => r.registryIdentifier,
			},
			{
				field: 'registryOwnerId',
				width: 250,
				renderer: (r) => r.registryOwnerId,
			},
			{
				field: 'registryType',
				width: 250,
				renderer: (r) => r.registryType,
			},
			{
				field: 'unitType',
				width: 250,
				renderer: (r) => r.unitType,
			},
		];

		if (this.selectedLayout == DataRegistryLayout.Service) {
			columns.splice(
				0,
				0,
				getSourceNameColumn(i18n('Service name'), true),
				getInstanceNameColumn(i18n('Servicequalifier name')),
				getAccountNameColumn(),
				getAssetNameColumn(),
				getCategoryNodeColumn(),
				getCategoryPathColumn(),
			);
		} else if (this.selectedLayout == DataRegistryLayout.Monitor) {
			columns.splice(
				0,
				0,
				getSourceNameColumn(i18n('Monitor name'), true),
				getAccountNameColumn(),
				getAssetNameColumn(),
				getCategoryNodeColumn(),
				getCategoryPathColumn(),
				getInstanceNameColumn(),
			);
		} else {
			columns.splice(
				0,
				0,
				getAssetNameColumn(true),
				getCategoryNodeColumn(),
				getAccountNameColumn(),
				getCategoryPathColumn(),
				getInstanceNameColumn(),
				getSourceNameColumn(),
			);
		}

		return columns;
	}

	destroy = () => {
		this.mobx.destroy();
	};
}

const getAssetNameColumn = (fixed?: boolean): GridColumnConfig<DataRegistryEntry> => {
	return {
		field: 'assetName',
		width: 250,
		fixed: fixed ? 'left' : null,
		renderer: (r) => r.assetName,
	};
};

const getCategoryNodeColumn = (): GridColumnConfig<DataRegistryEntry> => ({
	field: 'categoryNode',
	width: 250,
	renderer: (r) => r.categoryNode,
});

const getAccountNameColumn = (): GridColumnConfig<DataRegistryEntry> => ({
	field: 'accountName',
	width: 250,
	renderer: (r) => r.accountName,
});

const getCategoryPathColumn = (): GridColumnConfig<DataRegistryEntry> => ({
	field: 'categoryPath',
	width: 250,
	renderer: (r) => r.categoryPath,
});

const getInstanceNameColumn = (title?: string): GridColumnConfig<DataRegistryEntry> => {
	return {
		title: title ?? i18n('Instance'),
		field: 'instanceName',
		width: 250,
		renderer: (r) => r.instanceName,
	};
};

const getSourceNameColumn = (title?: string, fixed?: boolean): GridColumnConfig<DataRegistryEntry> => {
	return {
		title: title ?? i18n('Source'),
		field: 'registryOwnerName',
		width: 250,
		fixed: fixed ? 'left' : null,
		renderer: (r) => r.registryOwnerName,
	};
};
